import React, { useState, useRef, useEffect } from "react";
import {
  TextField,
  TextFieldProps,
  IconButton,
  InputAdornment,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import DataObjectIcon from "@mui/icons-material/DataObject";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PopOutTextDialog, { VariableField } from "./PopOutTextDialog";
import AITextAssistantDialog from "./AITextAssistantDialog";

interface PopOutTextInputProps extends Omit<TextFieldProps, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  dialogTitle?: string;
  dialogDescription?: string;
  enablePopOut?: boolean;
  popOutRows?: number;
  maxLength?: number;
  variableFields?: VariableField[];
  customVariableFields?: VariableField[];
  // AI Assistant props
  aiContext?: string;
  getToken?: () => Promise<string>;
  companyId?: string;
  // Slash command trigger for variables
  enableSlashTrigger?: boolean;
}

const PopOutTextInput: React.FC<PopOutTextInputProps> = ({
  value,
  onChange,
  dialogTitle,
  dialogDescription,
  enablePopOut = true,
  popOutRows = 20,
  maxLength,
  variableFields = [],
  customVariableFields = [],
  aiContext,
  getToken,
  companyId,
  enableSlashTrigger = false,
  ...textFieldProps
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [variableMenuAnchorEl, setVariableMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [slashPosition, setSlashPosition] = useState<number | null>(null);
  const savedScrollTop = useRef<number>(0);

  const hasVariables = variableFields.length > 0 || customVariableFields.length > 0;
  const hasAiAssistant = !!getToken && !!companyId;

  // Restore cursor position after inserting variable
  useEffect(() => {
    if (cursorPosition !== null && textFieldRef.current) {
      const input = textFieldRef.current;
      input.focus();
      input.setSelectionRange(cursorPosition, cursorPosition);
      input.scrollTop = savedScrollTop.current;
      setCursorPosition(null);
    }
  }, [cursorPosition]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (!maxLength || newValue.length <= maxLength) {
      onChange(newValue);
    }

    // Check for "/" trigger when enableSlashTrigger is true
    if (
      enableSlashTrigger &&
      hasVariables &&
      textFieldRef.current &&
      newValue[textFieldRef.current.selectionStart - 1] === "/"
    ) {
      setSlashPosition(textFieldRef.current.selectionStart - 1);
      savedScrollTop.current = textFieldRef.current.scrollTop;
      setVariableMenuAnchorEl(textFieldRef.current);
    }
  };

  const handleInsertVariable = (key: string) => {
    const variableText = `{{${key}}}`;
    const textarea = textFieldRef.current;
    const currentValue = value ?? "";

    if (slashPosition !== null) {
      // Insert at slash position, replacing the "/"
      const before = currentValue.substring(0, slashPosition);
      const after = currentValue.substring(slashPosition + 1);
      const newValue = before + variableText + after;

      if (!maxLength || newValue.length <= maxLength) {
        onChange(newValue);
        setCursorPosition(slashPosition + variableText.length);
      }
      setSlashPosition(null);
    } else if (textarea) {
      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      const newValue = currentValue.slice(0, start) + variableText + currentValue.slice(end);

      if (!maxLength || newValue.length <= maxLength) {
        onChange(newValue);
        setCursorPosition(start + variableText.length);
      }
    } else {
      const newValue = currentValue + variableText;
      if (!maxLength || newValue.length <= maxLength) {
        onChange(newValue);
      }
    }

    setVariableMenuAnchorEl(null);
  };

  const handleMenuClose = () => {
    setVariableMenuAnchorEl(null);
    setSlashPosition(null);
  };

  // Ensure value is never null/undefined to prevent errors
  const safeValue = value ?? "";
  const characterCount = safeValue.length;

  const handleDialogSave = (newValue: string) => {
    onChange(newValue);
    setDialogOpen(false);
  };

  const handleAiAccept = (generatedText: string) => {
    onChange(generatedText);
    setAiDialogOpen(false);
  };

  const isMultiline = textFieldProps.multiline ?? false;
  const showPopOutButton = enablePopOut && isMultiline;

  // Only show helperText on TextField if we don't have variables (otherwise we show it below)
  const showHelperTextBelow = hasVariables || maxLength;

  return (
    <Box sx={{ width: "100%" }}>
      <TextField
        {...textFieldProps}
        inputRef={textFieldRef}
        value={safeValue}
        onChange={handleChange}
        error={maxLength ? characterCount > maxLength : textFieldProps.error}
        helperText={showHelperTextBelow ? undefined : textFieldProps.helperText}
        InputProps={{
          ...textFieldProps.InputProps,
          endAdornment: (showPopOutButton || hasAiAssistant) ? (
            <InputAdornment position="end">
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {hasAiAssistant && (
                  <Tooltip title="AI Writing Assistant">
                    <IconButton
                      onClick={() => setAiDialogOpen(true)}
                      size="small"
                    >
                      <AutoAwesomeIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {showPopOutButton && (
                  <Tooltip title="Expand editor">
                    <IconButton
                      onClick={() => setDialogOpen(true)}
                      size="small"
                    >
                      <OpenInFullIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </InputAdornment>
          ) : textFieldProps.InputProps?.endAdornment,
        }}
        sx={{
          ...textFieldProps.sx,
          "& .MuiInputBase-root": {
            position: "relative",
          },
        }}
      />

      {/* Row with Insert Variable button, helper text, and character count */}
      {showHelperTextBelow && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 0.5,
            mx: 1.75,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {hasVariables && (
              <>
                <Tooltip title="Insert personalization variable">
                  <Button
                    size="small"
                    startIcon={<DataObjectIcon />}
                    onClick={(e) => setVariableMenuAnchorEl(e.currentTarget)}
                    sx={{ textTransform: "none", ml: -1 }}
                  >
                    Insert Variable
                  </Button>
                </Tooltip>
                <Menu
                  anchorEl={variableMenuAnchorEl}
                  open={Boolean(variableMenuAnchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{ sx: { maxHeight: 400, width: 280 } }}
                >
                  {variableFields.length > 0 && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
                      >
                        Member Fields
                      </Typography>
                      {variableFields.map((field) => (
                        <MenuItem
                          key={field.key}
                          onClick={() => handleInsertVariable(field.key)}
                          dense
                        >
                          <ListItemIcon>
                            {field.icon || <DataObjectIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={`{{${field.key}}}`}
                            secondaryTypographyProps={{
                              variant: "caption",
                              fontFamily: "monospace",
                            }}
                          />
                        </MenuItem>
                      ))}
                    </>
                  )}
                  {customVariableFields.length > 0 && (
                    <>
                      {variableFields.length > 0 && <Divider sx={{ my: 1 }} />}
                      <Typography
                        variant="caption"
                        sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
                      >
                        Custom Fields
                      </Typography>
                      {customVariableFields.map((field) => (
                        <MenuItem
                          key={field.key}
                          onClick={() => handleInsertVariable(field.key)}
                          dense
                        >
                          <ListItemIcon>
                            {field.icon || <DataObjectIcon fontSize="small" />}
                          </ListItemIcon>
                          <ListItemText
                            primary={field.label}
                            secondary={`{{${field.key}}}`}
                            secondaryTypographyProps={{
                              variant: "caption",
                              fontFamily: "monospace",
                            }}
                          />
                        </MenuItem>
                      ))}
                    </>
                  )}
                </Menu>
              </>
            )}
            {enableSlashTrigger && hasVariables && (
              <Typography variant="caption" color="text.secondary">
                Type{" "}
                <span
                  style={{
                    backgroundColor: "rgba(128, 128, 128, 0.2)",
                    padding: "2px 4px",
                    borderRadius: "4px",
                  }}
                >
                  /
                </span>{" "}
                to insert variables
              </Typography>
            )}
            {textFieldProps.helperText && (
              <Typography variant="caption" color="text.secondary">
                {textFieldProps.helperText}
              </Typography>
            )}
          </Box>
          {maxLength && (
            <Typography
              variant="caption"
              color={characterCount > maxLength ? "error" : "text.secondary"}
            >
              {characterCount} / {maxLength}
            </Typography>
          )}
        </Box>
      )}

      {showPopOutButton && (
        <PopOutTextDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleDialogSave}
          value={safeValue}
          title={dialogTitle || textFieldProps.label?.toString() || "Edit Text"}
          description={dialogDescription || textFieldProps.helperText?.toString()}
          placeholder={textFieldProps.placeholder}
          rows={popOutRows}
          maxLength={maxLength}
          variableFields={variableFields}
          customVariableFields={customVariableFields}
          aiContext={aiContext}
          getToken={getToken}
          companyId={companyId}
          enableSlashTrigger={enableSlashTrigger}
        />
      )}

      {/* AI Text Assistant Dialog for main input */}
      {hasAiAssistant && getToken && companyId && (
        <AITextAssistantDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          onAccept={handleAiAccept}
          existingText={safeValue}
          fieldContext={aiContext || dialogTitle || textFieldProps.label?.toString() || ""}
          getToken={getToken}
          companyId={companyId}
        />
      )}
    </Box>
  );
};

export default PopOutTextInput;